import React, { createContext, useContext } from "react";

// ─── Selection Context ────────────────────────────────────────────────

interface ListContextValue {
    selected: string | string[];
    onSelect?: (key: string) => void;
}

const ListContext = createContext<ListContextValue | null>(null);

function useListContext(): ListContextValue | null {
    return useContext(ListContext);
}

// ─── List ─────────────────────────────────────────────────────────────

interface ListProps {
    /** Selection mode. false = no selection (just hoverable/clickable rows). */
    selectable?: "single" | "multi" | false;
    /** Currently selected key(s). Controlled by the consumer. */
    selected?: string | string[];
    /** Called when an item is clicked (passes the itemKey). */
    onSelect?: (key: string) => void;
    /**
     * Which state to render. `"populated"` (default) shows `children`;
     * `"loading"` / `"error"` / `"empty"` show the matching slot instead.
     * The Container decides the state from its data; the domain supplies
     * each slot's content.
     */
    state?: "populated" | "loading" | "error" | "empty";
    /** Slot shown when `state="empty"` (e.g. "No items in this view."). */
    empty?: React.ReactNode;
    /** Slot shown when `state="loading"` (e.g. a skeleton). */
    loading?: React.ReactNode;
    /** Slot shown when `state="error"` (e.g. an error message + retry). */
    error?: React.ReactNode;
    children: React.ReactNode;
    /** Optional className. */
    className?: string;
}

/**
 * @version 1.1.0
 * @published 2026-04-08
 * @history
 *   1.1.0 — Add state slots. `state` ("populated" | "loading" | "error" |
 *           "empty") picks between `children` and the `loading` / `error` /
 *           `empty` slots. The Container decides the state from its data; the
 *           domain supplies each slot's content. Backward-compatible —
 *           defaults to "populated" (renders children).
 *   1.0.0 — Initial publish. Container that provides selection context to
 *           `ListItem` children. Supports `single`, `multi`, or no selection.
 */
export function List({
    selectable = false,
    selected = [],
    onSelect,
    state = "populated",
    empty,
    loading,
    error,
    children,
    className
}: ListProps): React.ReactElement {
    const ctx: ListContextValue | null = selectable
        ? { selected, onSelect }
        : null;

    // The Container drives `state`; the domain fills each slot. Default
    // "populated" preserves the pre-1.1.0 behaviour (render children).
    const body =
        state === "loading"
            ? loading
            : state === "error"
              ? error
              : state === "empty"
                ? empty
                : children;

    return (
        <ListContext.Provider value={ctx}>
            <div className={className}>{body}</div>
        </ListContext.Provider>
    );
}

// ─── ListItem ─────────────────────────────────────────────────────────

interface ListItemProps {
    /** Unique key for selection tracking. */
    itemKey: string;
    /** Element before the content (icon, IndicationUnit, token image, etc.). */
    leading?: React.ReactNode;
    /** Element after the content (value, multiplier, chevron, etc.). */
    trailing?: React.ReactNode;
    /** Render as a link instead of div. */
    href?: string;
    /** Open link in new tab. */
    external?: boolean;
    /** Override click handler (used when List is not selectable). */
    onClick?: () => void;
    children: React.ReactNode;
    /** Optional className. */
    className?: string;
    /** Vertical alignment of children. Defaults to `center`. */
    align?: "center" | "start";
    /** Gap between children. Defaults to `gap-3`. */
    gap?: string;
    /** Padding override. Defaults to `py-3 px-2`. */
    padding?: string;
    /** Bottom border color. Defaults to `border-neutral` token. */
    borderColor?: string;
    /** Background fill color on hover. Defaults to `border-neutral` token value. */
    hoverFillColor?: string;
    /** Top accent line color on hover. Defaults to ember (`accent`). Pass an
     *  inline color value to override (e.g. olive tint). */
    hoverLineColor?: string;
    /** Sim-only — force the row's HOVER decorations (accent line + fill) to
     *  their hovered state with no pointer present. CSS `group-hover:` can't be
     *  driven from JS, so scripted cursor tours flip this to paint the row
     *  hover. Behaves like `isSelected` visually (line + fill at full extent)
     *  but is transient (driven by the sim, not selection). Default `false`. */
    forceHover?: boolean;
}

/**
 * @version 1.0.0
 * @published 2026-04-08
 * @history
 *   1.0.0 — Initial publish. Row primitive with the ember accent line that fills
 *           left-to-right on hover and a background that fills top-to-bottom.
 *           Replaces `HoverRow`. When used inside a `List` with selection enabled,
 *           selected items keep the hover treatment persistently visible.
 *           Renderable as `<a>` (when `href` is provided) or `<div>`.
 */
export function ListItem({
    itemKey,
    leading,
    trailing,
    href,
    external = false,
    onClick,
    children,
    className,
    align = "center",
    gap = "gap-3",
    padding = "py-3 px-2",
    borderColor = "rgb(var(--color-line-neutral))",
    hoverFillColor = "rgb(var(--color-line-neutral))",
    hoverLineColor,
    forceHover = false
}: ListItemProps): React.ReactElement {
    const ctx = useListContext();

    const isSelected = ctx
        ? Array.isArray(ctx.selected)
            ? ctx.selected.includes(itemKey)
            : ctx.selected === itemKey
        : false;

    // The sim forces the hover decorations by treating the row as visually
    // "active" (line + fill at full extent), same as `isSelected`.
    const hovered = isSelected || forceHover;

    const handleClick = (): void => {
        if (ctx?.onSelect) {
            ctx.onSelect(itemKey);
        }
        if (onClick) {
            onClick();
        }
    };

    const alignClass = align === "start" ? "items-start" : "items-center";
    // The pointer only where the row does something on click (selectable,
    // its own handler, or a link). A display-only row keeps the default
    // cursor; its own buttons and links still show theirs.
    const interactive = Boolean(ctx?.onSelect || onClick || href);
    const baseClasses = `group/listitem relative flex ${gap} ${alignClass} ${padding} ${
        interactive ? "cursor-pointer" : "cursor-default"
    } no-underline ${className ?? ""}`;

    const decorations = (
        <>
            {/* Top line — accent fill left-to-right on hover (persistent when
                selected). Sits at -1px so it overlays (replaces) the
                resting top border rather than stacking 1px below it. */}
            <span
                className={`absolute -top-px left-0 h-px transition-all duration-300 ease-out z-10 ${
                    hoverLineColor ? "" : "bg-accent"
                } ${hovered ? "w-full" : "w-0 group-hover/listitem:w-full"}`}
                style={
                    hoverLineColor
                        ? { backgroundColor: hoverLineColor }
                        : undefined
                }
            />
            {/* Background — fills top-to-bottom on hover (persistent when selected) */}
            <span
                className={`absolute inset-x-0 top-0 transition-all duration-300 ease-out z-0 ${
                    hovered ? "h-full" : "h-0 group-hover/listitem:h-full"
                }`}
                style={{ backgroundColor: hoverFillColor }}
            />
        </>
    );

    const content = (
        <>
            {decorations}
            {leading && <span className="relative z-10">{leading}</span>}
            <span className="relative z-10 flex-1 flex items-center">
                {children}
            </span>
            {trailing && <span className="relative z-10">{trailing}</span>}
        </>
    );

    const borderStyle = {
        borderTop: `1px solid ${borderColor}`,
        borderBottom: `1px solid ${borderColor}`,
        marginTop: "-1px"
    };

    if (href) {
        return (
            <a
                href={href}
                target={external ? "_blank" : undefined}
                rel={external ? "noopener noreferrer" : undefined}
                className={baseClasses}
                style={borderStyle}
                onClick={handleClick}
            >
                {content}
            </a>
        );
    }

    return (
        <div className={baseClasses} style={borderStyle} onClick={handleClick}>
            {content}
        </div>
    );
}
